import { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { MetadataError } from '@ep/error-handling';
import readiness_gauge_img from '../../assets/home_dashboard/readiness_gauge.webp';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { ReadinessGaugeInstance } from '@esg/esg-global-types';
import ReadinessGaugeHub from '../../components/readiness_gauge/ReadinessGaugeHub';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { getReadinessGaugeInstances } from '../../lib/readiness_gauge/readiness_gauge_instance';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import AccessDenied from '../shared/AccessDenied';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import { UserContext } from '../../context/UserContext';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import PrimarySkeleton from '../../components/shared/loading/PrimarySkeleton';
import { Heading, Title } from '../../components/shared/typography/PrimaryTypography';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';

/**
 * View for displaying the entry point for the Readiness Gauge for Groups with and without existing Instances.
 * @returns {JSX.Element}
 */
const ReadinessGaugeDashboard = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { user } = useContext(UserContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking('Sustainability', 'Readiness Gauge Dashboard', group.id, company.id);

  const [access, setAccess] = useState<ViewAccess>(null);
  const [readinessGaugeInstances, setReadinessGaugeInstances] = useState<
    Array<ReadinessGaugeInstance>
  >([]);
  const [loading, setLoading] = useState<boolean>(true);

  const view_access: ViewAccess =
    user && group && company
      ? getViewAccess(user, group.id, company.id, 'sustainability.readiness_gauge')
      : null;
  const view_only: boolean = view_access === 'read';

  const navigate: NavigateFunction = useNavigate();

  const handleCreateClick = (): void => {
    navigate('/sustainability/readiness/questionnaire');
  };

  const handleUpdateReadinessGaugeInstances = (
    updated_readiness_gauge_instances: Array<ReadinessGaugeInstance>
  ): void => {
    setReadinessGaugeInstances(updated_readiness_gauge_instances);
  };

  const fetchReadinessGaugeInstances = async (): Promise<void> => {
    setLoading(true);
    try {
      // Load readiness gauges from database.
      if (group && company) {
        const readiness_gauge_instances: Array<ReadinessGaugeInstance> =
          await getReadinessGaugeInstances(group.id);
        // Load readiness gauges to memory.
        handleUpdateReadinessGaugeInstances(readiness_gauge_instances);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ReadinessGaugeDashboard failed on an unknown error while calling fetchReadinessGaugeInstances.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch Readiness Gauges. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      (async () => {
        setReadinessGaugeInstances([]);
        fetchReadinessGaugeInstances();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ReadinessGaugeDashboard failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [group, company]);

  useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'sustainability.readiness_gauge')
        : null
    );
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <>
      {loading || !group ? (
        <PrimarySkeleton height={150} variant="rounded" width={'100%'} />
      ) : readinessGaugeInstances.length > 0 ? (
        <Box>
          <ReadinessGaugeHub
            readiness_gauge_instances={readinessGaugeInstances}
            handleUpdateReadinessGaugeInstances={handleUpdateReadinessGaugeInstances}
          />
        </Box>
      ) : (
        <Box
          sx={{
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              alignItems: 'center'
            }}
          >
            <img src={readiness_gauge_img} width="250px" />
            <Title weight="Bold" sx={{ mt: 8, mb: 3 }}>
              Welcome to your Readiness Gauge
            </Title>
          </Box>
          <Heading weight="Light" sx={{ mb: 12 }}>
            Immediate insight into your current ESG compliance
          </Heading>
          <PrimaryButton
            endIcon={<ChevronRightIcon />}
            onClick={handleCreateClick}
            sx={{
              height: '8%',
              fontSize: '1rem',
              minWidth: 400,
              my: 3
            }}
            disabled={view_only}
          >
            Get Started
          </PrimaryButton>
        </Box>
      )}
    </>
  ) : (
    <AccessDenied />
  );
};

export default ReadinessGaugeDashboard;
